import type { DiagnosticSnapshot } from "@/api/diagnostic";

import { adherenceIndex, type AdherenceIndexState } from "../data/adherenceIndex";
import { commercialOffer, type CommercialOffer } from "../data/commercialOffer";
import { awaitingPricingAppBom, currentBom } from "../data/currentBom";
import { scopeItemsTile } from "../data/scopeItems";
import {
  decisionDestination,
  developmentsAnalyzed,
  wricefDecisions,
  type DecisionOrUndecided,
} from "../data/wricefDecisions";
import {
  companiesTile,
  environmentOverview,
  investmentComposition,
  projectTimeline,
  usageProfile,
  type ProjectTimeline,
  type UsageProfile,
} from "../diagnosticData";
import { chartOfAccountsFrame, type ChartOfAccountsFrame } from "../selectors/chartOfAccounts";

/**
 * O que o Resumo sabe dizer (EF-DIAG-004).
 *
 * **Não calcula nada.** Cada campo vem do mesmo seletor que o ato de origem usa,
 * e o que ainda não existe fica como estado da fonte (`undefined`, `unknown`,
 * `awaiting_*`), para a tela dizer "Preliminar" ou "Aguardando calibragem
 * comercial" em vez de mostrar 0 ou 100%.
 */
export interface DiagnosticSummary {
  /** Bloco 1 · Ato 1. */
  environment: {
    activeUsers: number | undefined;
    /** As empresas do cliente, sem as empresas-modelo da SAP. */
    companies: number | undefined;
    scopeItems: { active: number; lobCount: number | undefined } | undefined;
    usageProfile: UsageProfile | undefined;
  };
  /** Bloco 2 · Ato 1 (aderência) e plano de contas. */
  diagnosis: {
    adherence: AdherenceIndexState;
    chartOfAccounts: ChartOfAccountsFrame | null;
  };
  /** Bloco 3 · decisão do arquiteto sobre o WRICEF. */
  treatment: TreatmentSummary;
  /** Bloco 4 · Ato 4. */
  investment: {
    software: SoftwareSummary;
    project: CommercialOffer;
    timeline: ProjectTimeline | null;
  };
}

/**
 * - `decided`: há decisão do arquiteto. `leavingOrStandard` soma o que sai do
 *   escopo e o que a SAP já cobre no padrão.
 * - `awaiting_decision`: nenhuma decisão registrada. O universo, quando o
 *   catálogo o dá, ainda é conhecido; os convertidos não são.
 */
export type TreatmentSummary =
  | {
      state: "decided";
      analyzed: number;
      inConversion: number;
      leavingOrStandard: number;
      byDecision: { decision: DecisionOrUndecided; value: number; pct: number | undefined }[];
    }
  | { state: "awaiting_decision"; analyzed: number | undefined };

/**
 * O software do Ato 4, nos mesmos quatro casos dele: a BOM do Pricing App, a
 * espera por ela, a BOM calculada (só tem o anual) ou nada.
 */
export type SoftwareSummary =
  | { state: "pricing_app"; tcv: number; acv: number; periodMonths: number | undefined }
  | { state: "awaiting_pricing_app" }
  | { state: "calculated"; acv: number }
  | { state: "unavailable" };

export function diagnosticSummary({
  sections,
  validation,
  calibration,
}: {
  sections: DiagnosticSnapshot["sections"];
  validation?: DiagnosticSnapshot["validation"];
  calibration?: DiagnosticSnapshot["calibration"];
}): DiagnosticSummary {
  const companies = companiesTile(validation);
  const scopeItems = scopeItemsTile(validation);

  return {
    environment: {
      activeUsers: environmentOverview(sections).activeUsers.value,
      companies: companies.kind === "classified" ? companies.client : undefined,
      scopeItems:
        scopeItems.kind === "counted"
          ? { active: scopeItems.active, lobCount: scopeItems.lobCount }
          : undefined,
      usageProfile: usageProfile(sections),
    },
    diagnosis: {
      adherence: adherenceIndex(validation),
      chartOfAccounts: chartOfAccountsFrame(validation),
    },
    treatment: treatmentSummary(sections, validation),
    investment: {
      software: softwareSummary(sections, validation),
      project: commercialOffer(validation),
      timeline: projectTimeline(calibration),
    },
  };
}

function treatmentSummary(
  sections: DiagnosticSnapshot["sections"],
  validation: DiagnosticSnapshot["validation"] | undefined,
): TreatmentSummary {
  const frame = wricefDecisions(validation);
  if (!frame) {
    return { state: "awaiting_decision", analyzed: developmentsAnalyzed(sections, validation) };
  }
  return {
    state: "decided",
    analyzed: frame.universe,
    inConversion: frame.byDecision.in_scope ?? 0,
    leavingOrStandard:
      (frame.byDecision.out_of_scope ?? 0) + (frame.byDecision.covered_by_standard ?? 0),
    byDecision: decisionDestination(frame).segments.map((segment) => ({
      decision: segment.key as DecisionOrUndecided,
      value: segment.count,
      pct: segment.pct,
    })),
  };
}

function softwareSummary(
  sections: DiagnosticSnapshot["sections"],
  validation: DiagnosticSnapshot["validation"] | undefined,
): SoftwareSummary {
  const bom = currentBom(validation);
  if (bom) {
    return { state: "pricing_app", tcv: bom.tcv, acv: bom.acv, periodMonths: bom.periodMonths };
  }
  if (awaitingPricingAppBom(validation)) return { state: "awaiting_pricing_app" };
  const annual = investmentComposition(sections).annual.value;
  return annual === undefined ? { state: "unavailable" } : { state: "calculated", acv: annual };
}
